import type { Ref } from 'vue';

import { onBeforeUnmount, onMounted, ref } from 'vue';

export const useScroll = (container: Ref<HTMLElement | null>) => {
  const scrollLeft = ref(0);
  const endOfScroll = ref(false);
  const endOfScrollCallback = ref<((scrollLeftRatio: number) => void) | null>(
    null
  );

  const checkEndOfScroll = () => {
    if (!container.value) {
      return;
    }

    endOfScroll.value =
      scrollLeft.value >=
      container.value.scrollWidth - container.value.clientWidth;
  };

  const slideLeft = () => {
    if (scrollLeft.value > 0 && container.value) {
      scrollTo(scrollLeft.value - container.value.offsetWidth);
    }
  };

  const slideRight = () => {
    if (container.value) {
      scrollTo(scrollLeft.value + container.value.offsetWidth);
    }
  };

  const scrollToView = (offsetLeft: number) => {
    if (!container.value) {
      return;
    }

    let left = offsetLeft - container.value.offsetWidth / 2;
    left -= container.value.offsetLeft;

    scrollTo(left);
  };

  const scrollTo = (left: number) => {
    if (!container.value) {
      return;
    }

    container.value.scrollTo({
      left,
      behavior: 'smooth'
    });

    checkEndOfScroll();
  };

  const onContainerScroll = () => {
    if (container.value && endOfScrollCallback.value) {
      scrollLeft.value = Math.ceil(container.value.scrollLeft);
      const width = container.value.clientWidth;
      const scrollLeftRatio = scrollLeft.value / (width * 2);
      checkEndOfScroll();
      endOfScrollCallback.value(scrollLeftRatio);
    }
  };

  const onEndOfScroll = (callback: (scrollLeftRatio: number) => void) => {
    endOfScrollCallback.value = callback;
  };

  onMounted(() => {
    if (container.value) {
      container.value.addEventListener('scroll', onContainerScroll);
    }
  });

  onBeforeUnmount(() => {
    if (container.value) {
      container.value.removeEventListener('scroll', onContainerScroll);
    }
  });

  return {
    scrollLeft,
    endOfScroll,
    slideLeft,
    slideRight,
    scrollToView,
    onEndOfScroll
  };
};
